import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Crown, Users, Building2, Shield, UserCog, Trash2, Rocket, Sparkles } from "lucide-react";
import { toast } from "sonner";

const AdminPortal = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impLoading, setImpLoading] = useState(null); // tenant_id currently impersonating
  const [planDialog, setPlanDialog] = useState({ open: false, tenant: null, plan: "pro", expires: "" });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tenant: null, confirm: "" });
  const [deleting, setDeleting] = useState(false);
  const [launchingDemo, setLaunchingDemo] = useState(false);

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const res = await apiClient.get('/admin/tenants');
      setTenants(res.data.tenants);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Rich-demo seed on an existing tenant. Idempotent — the endpoint wipes
  // and re-lays only `seed_tag=seed:demo` docs so real customer data is
  // preserved even if the super admin accidentally hits this on a live
  // tenant. Kept as a backend endpoint (no UI trigger — real tenants
  // should never be mixed with demo data by accident).

  // Quick-launch: spin up an entirely fresh demo tenant, seed it, and
  // impersonate the disposable demo owner (NOT the super admin's own
  // user). Backend creates a synthetic `demo owner` user scoped to the
  // demo tenant, and we impersonate that user via the existing proven
  // impersonation endpoint — which mints an `imp_` prefixed session
  // token so "Stop Impersonating" works correctly and Layout doesn't
  // trap the super admin in a super-admin-only workspace.
  //
  // Early-Feb regression this fixes: an earlier version made the super
  // admin the owner of the demo tenant, which meant impersonation was a
  // no-op → user landed on /dashboard as themselves → app saw them as a
  // no-tenant user and sent them to the onboarding "enter company name"
  // form → completing that form attached the super admin's user record
  // to a new tenant, breaking their super-admin flow on next login.
  const handleLaunchDemo = async () => {
    setLaunchingDemo(true);
    const original = localStorage.getItem("session_token");
    try {
      const res = await apiClient.post('/admin/demo-tenants');
      const newTenantId = res.data?.tenant_id;
      const demoOwnerId = res.data?.demo_owner_id;
      if (!newTenantId || !demoOwnerId) throw new Error("Malformed demo tenant response");
      const impRes = await apiClient.post(
        `/admin/tenants/${newTenantId}/impersonate?as_user_id=${encodeURIComponent(demoOwnerId)}`
      );
      const newToken = impRes.data.session_token;
      if (original) localStorage.setItem("admin_original_token", original);
      localStorage.setItem("admin_return_path", "/admin/tenants");
      localStorage.setItem("session_token", newToken);
      await checkAuth();
      toast.success(`Demo tenant launched · ${res.data?.summary?.employees || 0} employees seeded`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (original) localStorage.setItem("session_token", original);
      toast.error(err.response?.data?.detail || err.message || "Failed to launch demo tenant");
      setLaunchingDemo(false);
    }
  };

  const handleImpersonate = async (tenant) => {
    setImpLoading(tenant.tenant_id);
    const original = localStorage.getItem("session_token");
    try {
      const res = await apiClient.post(`/admin/tenants/${tenant.tenant_id}/impersonate`);
      const newToken = res.data.session_token;
      // Verify the impersonation token resolves to a real user before committing
      const verify = await fetch(`${apiClient.defaults.baseURL}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      if (!verify.ok) throw new Error("Impersonation token rejected — tenant may be orphaned.");
      if (original) localStorage.setItem("admin_original_token", original);
      localStorage.setItem("admin_return_path", window.location.pathname);
      localStorage.setItem("session_token", newToken);
      // IMPORTANT: refresh AuthContext with the new impersonating user BEFORE navigating.
      // Layout.jsx has a guard that redirects super admins off non-/admin routes; without
      // waiting for checkAuth, React renders Layout with the stale super-admin user and
      // bounces us back to /admin (the "flash and disappear" symptom).
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      if (original) localStorage.setItem("session_token", original);
      toast.error(e.response?.data?.detail || e.message || "Impersonation failed");
      setImpLoading(null);
    }
  };

  const handleUpdatePlan = async () => {
    try {
      await apiClient.put(`/admin/tenants/${planDialog.tenant.tenant_id}/plan`, {
        plan: planDialog.plan,
        plan_expires_at: planDialog.expires || null
      });
      toast.success(`Plan updated to ${planDialog.plan}`);
      setPlanDialog({ open: false, tenant: null, plan: "pro", expires: "" });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update plan");
    }
  };

  const handleDeleteTenant = async () => {
    const t = deleteDialog.tenant;
    if (!t) return;
    setDeleting(true);
    try {
      const res = await apiClient.delete(
        `/admin/tenants/${t.tenant_id}?confirm=${encodeURIComponent(t.company_name)}`
      );
      const counts = res.data?.counts || {};
      const total = Object.values(counts).reduce((a, b) => a + (typeof b === "number" && b > 0 ? b : 0), 0);
      toast.success(`Deleted ${t.company_name} · ${total} records purged`);
      setDeleteDialog({ open: false, tenant: null, confirm: "" });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete tenant");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-portal">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] tracking-tight font-['Manrope']">Tenants</h1>
            <p className="text-sm text-slate-500 mt-1">{tenants.length} tenants total · click a row for full detail</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-[#0D9488] hover:bg-[#14B8A6] text-white shadow-sm"
              onClick={handleLaunchDemo}
              disabled={launchingDemo}
              data-testid="launch-demo-tenant-btn"
              title="Spin up a fresh demo tenant preseeded with realistic data and drop you inside it"
            >
              <Rocket className="w-3.5 h-3.5 mr-1.5" />
              {launchingDemo ? "Launching..." : "Launch demo tenant"}
            </Button>
            <Badge className="bg-amber-100 text-amber-700"><Crown className="w-3 h-3 mr-1" /> {user?.email}</Badge>
          </div>
        </div>

        {/* Tenants Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#0D9488]" /> All Tenants
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Owner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Plan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Employees</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Source</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Expires</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Created</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={8}><div className="h-10 bg-slate-100 rounded animate-pulse"></div></TableCell></TableRow>
                  ))
                ) : tenants.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No tenants</TableCell></TableRow>
                ) : (
                  tenants.map(t => (
                    <TableRow
                      key={t.tenant_id}
                      data-testid="admin-tenant-row"
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/admin/tenants/${t.tenant_id}`)}
                    >
                      <TableCell className="font-medium text-[#0A192F]">
                        <div className="flex items-center gap-1.5">
                          {t.company_name}
                          {t.is_demo && (
                            <Badge
                              className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100 text-[10px] px-1.5 py-0"
                              data-testid={`tenant-demo-badge-${t.tenant_id}`}
                              title="Seeded demo tenant — safe to delete when done"
                            >
                              <Sparkles className="w-2.5 h-2.5 mr-0.5" /> Demo
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{t.owner_name}</p>
                          <p className="text-xs text-slate-400">{t.owner_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {t.plan === "pro" ? (
                          <Badge className="bg-amber-100 text-amber-700"><Crown className="w-3 h-3 mr-1" /> Pro</Badge>
                        ) : t.plan === "starter" ? (
                          <Badge className="bg-blue-100 text-blue-700">Starter</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Free</Badge>
                        )}
                        {t.applied_discount_code && (
                          <Badge
                            className="ml-1.5 bg-teal-100 text-teal-800 hover:bg-teal-100 font-mono text-[10px] px-1.5 py-0"
                            title={`Applied ${t.applied_discount_at ? formatDate(t.applied_discount_at) : ""}`}
                            data-testid={`tenant-discount-${t.tenant_id}`}
                          >
                            {t.applied_discount_code}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{t.employee_count}</span>
                        <span className="text-slate-400 text-xs ml-1">/ {t.total_users} users</span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 capitalize">{t.plan_source || "default"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{t.plan_expires_at ? formatDate(t.plan_expires_at) : <span className="text-slate-300">Permanent</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(t.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            disabled={impLoading === t.tenant_id}
                            onClick={(e) => { e.stopPropagation(); handleImpersonate(t); }}
                            data-testid={`admin-impersonate-${t.tenant_id}`}
                          >
                            <UserCog className="w-3 h-3 mr-1" />
                            {impLoading === t.tenant_id ? "..." : "Impersonate"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setPlanDialog({
                              open: true,
                              tenant: t,
                              plan: t.plan || "free",
                              expires: ""
                            }); }}
                            data-testid={`admin-plan-${t.tenant_id}`}
                          >
                            Change Plan
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-200 text-rose-600 hover:bg-rose-50"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, tenant: t, confirm: "" }); }}
                            data-testid={`admin-delete-${t.tenant_id}`}
                            title="Delete tenant (destructive)"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Plan Update Dialog */}
      <Dialog open={planDialog.open} onOpenChange={(open) => !open && setPlanDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0A192F]">
              {planDialog.plan === "pro" ? "Grant Pro Access" : "Downgrade to Free"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {planDialog.tenant?.company_name} ({planDialog.tenant?.owner_email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={planDialog.plan} onValueChange={(v) => setPlanDialog(d => ({ ...d, plan: v }))}>
                <SelectTrigger data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free ($0)</SelectItem>
                  <SelectItem value="starter">Starter ($59/mo)</SelectItem>
                  <SelectItem value="pro">Pro ($99/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {planDialog.plan !== "free" && (
              <div className="space-y-2">
                <Label>Expires (leave blank for permanent)</Label>
                <Input type="date" value={planDialog.expires} onChange={(e) => setPlanDialog(d => ({ ...d, expires: e.target.value }))} data-testid="admin-plan-expires" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(d => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="bg-[#0D9488] hover:bg-[#14B8A6]" onClick={handleUpdatePlan} data-testid="admin-plan-confirm">
              {planDialog.plan === "free" ? "Downgrade to Free" : `Grant ${planDialog.plan.charAt(0).toUpperCase() + planDialog.plan.slice(1)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tenant Dialog — destructive, requires typing the company name */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, tenant: null, confirm: "" })}>
        <DialogContent className="sm:max-w-md" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete Tenant
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">
                This will <span className="font-bold text-rose-700">permanently delete</span>{" "}
                <span className="font-semibold text-[#0A192F]">{deleteDialog.tenant?.company_name}</span>{" "}
                and every record scoped to it — employees, time entries, payments, schedules,
                shifts, audit logs, invites, and all users on this tenant.
              </span>
              <span className="block text-rose-600 font-medium">
                This action cannot be undone.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>
              Type <span className="font-mono font-bold text-[#0A192F]">{deleteDialog.tenant?.company_name}</span> to confirm
            </Label>
            <Input
              value={deleteDialog.confirm}
              onChange={(e) => setDeleteDialog(d => ({ ...d, confirm: e.target.value }))}
              placeholder={deleteDialog.tenant?.company_name || ""}
              data-testid="admin-delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, tenant: null, confirm: "" })}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              onClick={handleDeleteTenant}
              disabled={deleting || deleteDialog.confirm.trim() !== (deleteDialog.tenant?.company_name || "").trim()}
              data-testid="admin-delete-confirm-btn"
            >
              {deleting ? "Deleting..." : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPortal;
